/**
 * Building blocks for a section of the preferences window (见 21), shared by the
 * platform's own pages and module-contributed sections so every page reads as
 * one window. Figma frames under `9229:*` (cards 14px, rows 54px, pane inset
 * 132px).
 *
 * Tokens: Color/text/{primary,secondary,tertiary,quaternary,error,anti,brand}
 * → `--app-color-text-*`; Color/background/gray100 80% → `--app-color-card`
 * (light value is `white-a80`); Color/background/gray00 4%/6%/8% →
 * `--app-base-black-a*`, paired with `dark:` `--app-base-white-a*` because the
 * base palette does not flip; text on brand/error fills → `--app-color-on-accent`;
 * Color/Extended/* → `--app-base-extended-*`; Color/brand → `--app-base-brand`.
 * The switch copies Figma Switch/MacOS 44×20 without backdrop-filter or
 * `transition-colors`. Overlay glass/blur is omitted (solid fill + drop-shadow).
 *
 * The press / enter / overlay motion classes live in this package's
 * `styles.css` (`.settings-row-press`, `.settings-motion-*`).
 */
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/utils';
import { Icon } from './icon';
import { useSettingsPaneChrome } from './settings-pane-chrome';

export const SETTINGS_CARD =
  'w-full overflow-hidden rounded-[14px] bg-(--app-color-card)';
export const SETTINGS_HAIRLINE =
  'border-(--app-base-black-a4) dark:border-(--app-base-white-a4)';
export const SETTINGS_PANE = 'flex w-full flex-col gap-6 px-[132px]';
export const SETTINGS_ROW =
  'flex min-h-[54px] w-full items-center justify-between px-5';

/**
 * Full-width row on a card. The highlight is an inset pill (see
 * `.settings-row-press`) so it does not repaint the card or the hairline.
 */
export const SETTINGS_CARD_PRESS = 'settings-row-press';

/** Small round control. Its own disc, one step above the surface it sits on. */
export const SETTINGS_ICON_PRESS =
  'cursor-pointer hover:bg-(--app-color-card-active) active:bg-(--app-base-black-a10) focus-visible:outline-2 focus-visible:outline-(--app-base-brand) disabled:cursor-default disabled:hover:bg-transparent disabled:active:bg-transparent dark:active:bg-(--app-base-white-a30)';

/** Neutral filled control that already paints 6%. The next steps stay opaque. */
export const SETTINGS_NEUTRAL_PRESS =
  'cursor-pointer hover:bg-(--app-base-black-a8) active:bg-(--app-base-black-a10) focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--app-base-brand) dark:hover:bg-(--app-base-white-a10) dark:active:bg-(--app-base-white-a20)';

export const SETTINGS_BRAND_PRESS =
  'cursor-pointer hover:bg-(--app-color-brand-hover) active:bg-(--app-color-brand-active) focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--app-base-brand)';

/** One section's content column: the 132px pane inset and 24px block rhythm. */
export function SettingsPane({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <div className={cn(SETTINGS_PANE, 'settings-motion-enter', className)}>
      {children}
    </div>
  );
}

export function SettingsSectionTitle({
  children,
  tone = 'primary',
}: {
  children: ReactNode;
  /** `secondary` is 08-body small / Color/text/secondary (idle sign-out). */
  tone?: 'primary' | 'secondary';
}): ReactNode {
  return (
    <p
      className={cn(
        'w-full px-2 pt-3.5 pb-3.5 font-medium',
        tone === 'secondary'
          ? 'text-[13px] leading-5 text-(--app-color-text-secondary)'
          : 'text-[14px] leading-[22px] text-(--app-color-text-primary)',
      )}
    >
      {children}
    </p>
  );
}

export function SettingsHint({ children }: { children: ReactNode }): ReactNode {
  return (
    <p className="w-full px-5 pt-1.5 text-[13px] leading-5 text-(--app-color-text-tertiary)">
      {children}
    </p>
  );
}

export function SettingsHairline(): ReactNode {
  return <span className={cn('h-px w-full border-t', SETTINGS_HAIRLINE)} />;
}

export function SettingsGlyph({
  id,
  size = 22,
  className,
}: {
  id: string;
  size?: number;
  className?: string;
}): ReactNode {
  return (
    <Icon
      spec={{ type: 'builtin', id }}
      size={size}
      className={cn('shrink-0', className)}
    />
  );
}

/** Figma Switch/MacOS 44×20. Thumb slides; track color snaps. */
export function SettingsSwitch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onChange(!checked);
        }
      }}
      className={cn(
        'relative h-5 w-11 shrink-0 rounded-full p-0.5',
        disabled
          ? 'cursor-default bg-(--app-base-black-a4) dark:bg-(--app-base-white-a4)'
          : checked
            ? 'cursor-pointer bg-(--app-base-brand) hover:opacity-90 active:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--app-base-brand)'
            : 'cursor-pointer bg-(--app-base-black-a8) hover:bg-(--app-base-black-a10) active:bg-(--app-base-black-a20) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--app-base-brand) dark:bg-(--app-base-white-a10) dark:hover:bg-(--app-base-white-a20) dark:active:bg-(--app-base-white-a30)',
      )}
    >
      <span
        className="block h-full w-[26px] rounded-full bg-(--app-base-white) shadow-[0_0_1px_0_rgba(0,0,0,0.05),0_0_4px_0_rgba(0,0,0,0.05),0_0_44px_0_rgba(0,0,0,0.1)] transition-transform duration-200 ease-out"
        style={{ transform: checked ? 'translateX(14px)' : 'translateX(0)' }}
      />
    </button>
  );
}

export function SettingsSwitchRow({
  icon,
  label,
  description,
  checked,
  disabled,
  last,
  onChange,
}: {
  icon?: string;
  label: string;
  /** 13/20 tertiary line under the label. */
  description?: string;
  checked: boolean;
  disabled?: boolean;
  last?: boolean;
  onChange: (next: boolean) => void;
}): ReactNode {
  const tone = disabled
    ? 'text-(--app-color-text-tertiary)'
    : 'text-(--app-color-text-primary)';
  return (
    <div className="flex w-full flex-col">
      <div className={cn(SETTINGS_ROW, 'gap-2 py-4')}>
        <span className="flex min-w-0 items-center gap-2.5">
          {icon ? (
            <SettingsGlyph id={icon} className={cn('size-[22px]', tone)} />
          ) : null}
          <span className="flex min-w-0 flex-col">
            <span className={cn('text-[14px] leading-[22px]', tone)}>
              {label}
            </span>
            {description ? (
              <span className="text-[13px] leading-5 text-(--app-color-text-tertiary)">
                {description}
              </span>
            ) : null}
          </span>
        </span>
        <SettingsSwitch
          checked={checked}
          disabled={disabled}
          label={label}
          onChange={onChange}
        />
      </div>
      {last ? null : <SettingsHairline />}
    </div>
  );
}

export function SettingsNavRow({
  icon,
  label,
  value,
  trailing,
  last,
  danger,
  disabled,
  busy,
  onClick,
}: {
  icon?: string;
  label: string;
  value?: string;
  trailing?: ReactNode;
  last?: boolean;
  danger?: boolean;
  disabled?: boolean;
  /** In-flight action. The row stays in place, refuses a second press, and reports busy. */
  busy?: boolean;
  onClick?: () => void;
}): ReactNode {
  const color = disabled
    ? 'text-(--app-color-text-tertiary)'
    : danger
      ? 'text-(--app-color-text-error)'
      : 'text-(--app-color-text-primary)';
  const pressable = Boolean(onClick) && !disabled && !busy;
  // A row with no action is not a control. The chevron slot stays so the
  // trailing value does not jump when the action arrives.
  const control = pressable || disabled || busy;
  const row = (
    <>
      <span className={cn('flex min-w-0 items-center gap-2.5', color)}>
        {icon ? (
          <SettingsGlyph id={icon} className={cn('size-[22px]', color)} />
        ) : null}
        <span className="truncate text-[14px] leading-[22px]">{label}</span>
      </span>
      <span className="flex min-w-0 items-center gap-1">
        {value ? (
          <span className="max-w-[280px] truncate text-[14px] leading-[22px] text-(--app-color-text-secondary)">
            {value}
          </span>
        ) : null}
        {trailing}
        <SettingsGlyph
          id="i-angle-right"
          size={20}
          className={cn(
            'size-5',
            control ? 'text-(--app-color-text-quaternary)' : 'invisible',
          )}
        />
      </span>
    </>
  );
  const rowClass = cn(
    SETTINGS_ROW,
    'gap-2 py-4 text-left',
    pressable && SETTINGS_CARD_PRESS,
    busy && 'cursor-progress disabled:cursor-progress',
    !pressable && !busy && 'cursor-default',
  );
  return (
    <div className="flex w-full flex-col">
      {control ? (
        <button
          type="button"
          disabled={disabled || busy}
          aria-busy={busy || undefined}
          className={rowClass}
          onClick={pressable ? onClick : undefined}
        >
          {row}
        </button>
      ) : (
        <div className={rowClass}>{row}</div>
      )}
      {last ? null : <SettingsHairline />}
    </div>
  );
}

export function SettingsCheckRow({
  label,
  checked,
  last,
  subtitle,
  stacked,
  disabled,
  onClick,
}: {
  label: string;
  checked: boolean;
  last?: boolean;
  subtitle?: string;
  /**
   * Language list `9229:187164`: title is 16/24 medium, description is 13px
   * Color/text/secondary. Single-line rows stay 14/22.
   */
  stacked?: boolean;
  disabled?: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        disabled={disabled}
        className={cn(
          SETTINGS_ROW,
          'gap-2 py-4 text-left',
          disabled ? 'cursor-default opacity-40' : SETTINGS_CARD_PRESS,
        )}
        onClick={onClick}
      >
        <span className="flex min-w-0 flex-col">
          <span
            className={
              stacked
                ? 'text-[16px] leading-6 font-medium text-(--app-color-text-primary)'
                : 'text-[14px] leading-[22px] text-(--app-color-text-primary)'
            }
          >
            {label}
          </span>
          {subtitle ? (
            <span className="text-[13px] leading-normal text-(--app-color-text-secondary)">
              {subtitle}
            </span>
          ) : null}
        </span>
        {checked ? (
          <span className="flex size-[22px] items-center justify-center rounded-full bg-(--app-base-brand)">
            <SettingsGlyph
              id="i-check"
              size={14}
              className="size-3.5 text-(--app-color-on-accent)"
            />
          </span>
        ) : (
          <span className="size-[22px] rounded-full border-[1.5px] border-(--app-color-border)" />
        )}
      </button>
      {last ? null : <SettingsHairline />}
    </div>
  );
}

export function SettingsBrandButton({
  children,
  disabled,
  wide,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  wide?: boolean;
  onClick?: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-10 items-center justify-center rounded-[10px] px-3 text-[15px] leading-6 text-(--app-color-on-accent)',
        wide ? 'w-full' : null,
        disabled
          ? 'cursor-default bg-(--app-base-brand-a40)'
          : cn('bg-(--app-base-brand)', SETTINGS_BRAND_PRESS),
      )}
    >
      {children}
    </button>
  );
}

export function SettingsGhostButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-10 flex-1 items-center justify-center rounded-[10px] bg-(--app-base-black-a6) px-3 text-[15px] leading-6 text-(--app-color-text-secondary) dark:bg-(--app-base-white-a6)',
        SETTINGS_NEUTRAL_PRESS,
      )}
    >
      {children}
    </button>
  );
}

export function SettingsSmallGhostButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-8 items-center justify-center rounded-[10px] bg-(--app-base-black-a6) px-2.5 text-[14px] leading-[22px] text-(--app-color-text-secondary) dark:bg-(--app-base-white-a6)',
        SETTINGS_NEUTRAL_PRESS,
      )}
    >
      {children}
    </button>
  );
}

export function SettingsSmallBrandButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 items-center justify-center rounded-[10px] bg-(--app-base-brand) px-2.5 text-[14px] leading-[22px] text-(--app-color-on-accent)',
        disabled ? 'cursor-default opacity-40' : SETTINGS_BRAND_PRESS,
      )}
    >
      {children}
    </button>
  );
}

export function SettingsSmallDangerButton({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 items-center justify-center rounded-[10px] bg-(--app-color-text-error) px-2.5 text-[14px] leading-[22px] text-(--app-color-on-accent)',
        disabled
          ? 'cursor-default opacity-40'
          : 'cursor-pointer hover:opacity-90 active:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--app-base-brand)',
      )}
    >
      {children}
    </button>
  );
}

/**
 * A sheet over the whole preferences window. Inside the window it is portaled
 * to the window's overlay layer, so a section's sheet covers the sidebar too;
 * elsewhere it covers its nearest positioned ancestor.
 */
export function SettingsOverlayScrim({
  children,
  onDismiss,
  width,
  dismissLabel,
}: {
  children: ReactNode;
  onDismiss: () => void;
  width: number;
  /** Accessible name of the backdrop button. */
  dismissLabel: string;
}): ReactNode {
  const chrome = useSettingsPaneChrome();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const sheet = (
    <div className="absolute inset-0 z-20 flex items-center justify-center">
      <button
        type="button"
        aria-label={dismissLabel}
        className={cn(
          'settings-overlay-scrim absolute inset-0 bg-(--app-base-black-a40) transition-opacity duration-200',
          shown ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onDismiss}
      />
      <div
        className="settings-motion-overlay relative min-w-0 overflow-hidden rounded-[20px] bg-(--app-color-bg-surface) shadow-[0_4px_20px_0_rgba(0,0,0,0.25)]"
        style={{ width }}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
  return chrome?.overlayRoot ? createPortal(sheet, chrome.overlayRoot) : sheet;
}

/**
 * Confirmation sheet, Figma `9229:186533`. Title and body share a padding/8
 * group; that group and the buttons are padding/16 apart. `onConfirm` may be
 * async: the sheet stays open and inert while it runs, closes when it resolves,
 * and stays open when it rejects (the caller reports the failure).
 */
export function SettingsConfirmSheet({
  title,
  body,
  confirm,
  cancelLabel,
  closeLabel,
  danger,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirm: string;
  cancelLabel: string;
  closeLabel: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}): ReactNode {
  const [pending, setPending] = useState(false);
  const run = (): void => {
    setPending(true);
    void Promise.resolve()
      .then(onConfirm)
      .then(
        () => onClose(),
        () => setPending(false),
      );
  };
  const dismiss = pending ? () => undefined : onClose;
  return (
    <SettingsOverlayScrim
      width={360}
      dismissLabel={closeLabel}
      onDismiss={dismiss}
    >
      <div className="flex w-full flex-col gap-(--app-padding-l) px-5 pt-4 pb-5">
        <div className="flex w-full flex-col gap-(--app-padding-2xs)">
          <div className="flex items-start gap-2">
            <p className="min-w-0 flex-1 pt-1.5 text-[16px] leading-6 font-medium text-(--app-color-text-primary)">
              {title}
            </p>
            <button
              type="button"
              className={cn(
                'flex size-[22px] items-center justify-center rounded-full',
                SETTINGS_ICON_PRESS,
              )}
              aria-label={closeLabel}
              onClick={dismiss}
            >
              <SettingsGlyph id="i-cancel" size={22} className="size-[22px]" />
            </button>
          </div>
          <p className="text-[14px] leading-[22px] text-(--app-color-text-primary)">
            {body}
          </p>
        </div>
        <div className="flex items-center justify-end gap-3">
          <SettingsSmallGhostButton onClick={dismiss}>
            {cancelLabel}
          </SettingsSmallGhostButton>
          {danger ? (
            <SettingsSmallDangerButton disabled={pending} onClick={run}>
              {confirm}
            </SettingsSmallDangerButton>
          ) : (
            <SettingsSmallBrandButton onClick={run}>
              {confirm}
            </SettingsSmallBrandButton>
          )}
        </div>
      </div>
    </SettingsOverlayScrim>
  );
}

export function SettingsRangeSlider({
  value,
  min,
  max,
  onChange,
  start,
  end,
  disabled,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  start?: ReactNode;
  end?: ReactNode;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  const pct = ((value - min) / (max - min)) * 100;
  const fill: CSSProperties = {
    background: `linear-gradient(to right, var(--app-base-brand) ${pct}%, var(--app-color-card-active) ${pct}%)`,
  };
  return (
    <div className="flex h-[54px] w-full items-center gap-3 px-5">
      {start}
      <label className="flex min-w-0 flex-1 items-center" htmlFor={id}>
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
          className={cn(
            'h-1.5 w-full appearance-none rounded-full [&::-webkit-slider-thumb]:h-7 [&::-webkit-slider-thumb]:w-11 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-(--app-base-white-a80) [&::-webkit-slider-thumb]:bg-(--app-color-bg-module-container) [&::-webkit-slider-thumb]:shadow-[0_2px_6px_0_rgba(0,0,0,0.12)]',
            disabled ? 'cursor-default opacity-40' : 'cursor-pointer',
          )}
          style={fill}
        />
      </label>
      {end}
    </div>
  );
}
